import {
  ReportScoreDescriptions,
  SkillScoreSections,
} from "@/pages/reports/reports-const";
// eslint-disable-next-line no-restricted-imports -- DO NOT COPY THIS LINE. See docs/lints.md.
import {
  PlayerMetadataScoreRow,
  PlayerRow,
  ReportQueryParams,
  ReportScore,
} from "@suns/api/generated-client/apollo";
import { Flex, Skeleton, Text } from "@suns/design-system";
import { useAccount, useAsync } from "@/shared/hooks";
import { apolloApi } from "@/shared/api";
import { AiEditor } from "./AiEditor";
import { notify } from "@/components/bugsnag";
import { SunsApiError } from "@suns/api";
import PlayerAttributes from "./PlayerAttributes";
import ScoreBarChart from "./ScoreBarChart";

export async function scoutingLoader(playerId: number) {
  const { summaries } = await apolloApi.getPlayerSummaries(playerId);
  const { reports } = await apolloApi.getReports({
    playerIds: [playerId],
    limit: 1,
    status: ReportQueryParams.status.PUBLISHED,
  });

  const filteredSummaries = summaries.filter(({ size }) => size === "MEDIUM");

  return {
    offenseSummary: filteredSummaries.find(({ type }) => type === "OFFENSE"),
    defenseSummary: filteredSummaries.find(({ type }) => type === "DEFENSE"),
    otherSummary: filteredSummaries.find(({ type }) => type === "OTHER"),
    hasReports: reports.length > 0,
  };
}

export function PlayerScouting({
  player,
}: {
  player: PlayerRow & { scores: PlayerMetadataScoreRow[] };
}) {
  const { loading, response, error } = useAsync(scoutingLoader, player.id);

  if (loading) {
    return <PlayerScoutingSkeleton />;
  }

  if (error) {
    if ((error as SunsApiError)?.authError) {
      throw error;
    }

    notify(error);

    return (
      <Text muted size="lg" className="py-4 text-center">
        Error loading scouting data. We've been notified.
      </Text>
    );
  }

  if (!player.metadata.length && !response?.hasReports) {
    return (
      <Text muted size="lg" className="py-4 text-center">
        No scouting data available.
      </Text>
    );
  }

  return (
    <Flex direction="down" gap="lg">
      <PlayerAttributes playerMetadata={player.metadata[0]!} />
      {response?.hasReports ? (
        <>
          <OffenseSection
            playerId={player.id}
            defaultSummary={response?.offenseSummary?.value}
            scores={player.scores}
          />
          <Flex direction="down" gap="md">
            <DefenseSection
              playerId={player.id}
              defaultSummary={response?.defenseSummary?.value}
              scores={player.scores}
            />
          </Flex>
          <OtherSection
            playerId={player.id}
            defaultSummary={response?.otherSummary?.value}
            scores={player.scores}
          />
        </>
      ) : null}
    </Flex>
  );
}

function PlayerScoutingSkeleton() {
  return (
    <Flex direction="down" gap="lg">
      <Skeleton className="h-[50px] w-1/2" />
      <Skeleton className="h-[250px] w-full" />
    </Flex>
  );
}

function OffenseSection({
  playerId,
  scores,
  defaultSummary,
}: {
  playerId: number;
  scores: PlayerMetadataScoreRow[];
  defaultSummary?: string;
}) {
  const { isScoutAdmin, isDeveloper } = useAccount();

  const hasEditAccess = isScoutAdmin || isDeveloper;

  const offenseScores = SkillScoreSections.OFFENSE.map(
    (section) => section.scores
  ).flat();
  const offenseScoresColumn1 = offenseScores.slice(0, 6).map((score) => ({
    key: score,
    value: scores?.find((s) => s.key === score)?.value ?? 0,
    label:
      ReportScoreDescriptions[score].fullLabel ||
      ReportScoreDescriptions[score].label,
  }));

  const offenseScoresColumn2 = offenseScores
    .slice(6, offenseScores.length)
    .map((score) => ({
      key: score,
      value: Math.round(scores?.find((s) => s.key === score)?.value ?? 0),
      label:
        ReportScoreDescriptions[score].fullLabel ||
        ReportScoreDescriptions[score].label,
    }));

  return (
    <Flex direction="down" gap="md">
      <AiEditor
        title="Offense"
        defaultValue={defaultSummary}
        readonly={!hasEditAccess}
        maxLength={1200}
        onGenerateAi={async () => {
          const result = await apolloApi.generatePlayerSummary({
            playerId,
            type: "OFFENSE",
            size: "MEDIUM",
          });

          return result;
        }}
        onSave={async (value) => {
          await apolloApi.updatePlayerSummary({
            playerId,
            type: "OFFENSE",
            size: "MEDIUM",
            value,
          });

          return true;
        }}
      />
      {scores.length ? (
        <Flex direction="right" gap="md" wrap>
          <ScoreBarChart
            labels={[1, 2, 3, 4, 5]}
            scores={offenseScoresColumn1 ?? []}
          />
          <ScoreBarChart
            labels={[1, 2, 3, 4, 5]}
            scores={offenseScoresColumn2 ?? []}
          />
        </Flex>
      ) : null}
    </Flex>
  );
}

function DefenseSection({
  playerId,
  scores,
  defaultSummary,
}: {
  playerId: number;
  scores: PlayerMetadataScoreRow[];
  defaultSummary?: string;
}) {
  const { isScoutAdmin, isDeveloper } = useAccount();

  const hasEditAccess = isScoutAdmin || isDeveloper;

  const defenseScores = SkillScoreSections.DEFENSE.map(
    (section) => section.scores
  )
    .flat()
    .map((score) => ({
      key: score,
      value: scores?.find((s) => s.key === score)?.value ?? 0,
      label:
        ReportScoreDescriptions[score].fullLabel ||
        ReportScoreDescriptions[score].label,
    }));

  return (
    <Flex direction="down" gap="md">
      <AiEditor
        title="Defense"
        defaultValue={defaultSummary}
        readonly={!hasEditAccess}
        maxLength={1200}
        onGenerateAi={async () => {
          const result = await apolloApi.generatePlayerSummary({
            playerId,
            type: "DEFENSE",
            size: "MEDIUM",
          });

          return result;
        }}
        onSave={async (value) => {
          await apolloApi.updatePlayerSummary({
            playerId,
            type: "DEFENSE",
            size: "MEDIUM",
            value,
          });

          return true;
        }}
      />
      {scores.length ? (
        <Flex direction="right" gap="md" wrap>
          <ScoreBarChart
            labels={[1, 2, 3, 4, 5]}
            scores={defenseScores ?? []}
          />
          <Flex className="flex-1">
            <CanDefendSection scores={scores ?? []} hideTitle={true} />
          </Flex>
        </Flex>
      ) : null}
    </Flex>
  );
}

export function CanDefendSection({
  scores,
  print = false,
}: {
  scores: PlayerMetadataScoreRow[];
  hideTitle?: boolean;
  print?: boolean;
}) {
  const chartData = [
    ReportScore.key.DEFEND_POINT_GUARD,
    ReportScore.key.DEFEND_GUARD,
    ReportScore.key.DEFEND_WING,
    ReportScore.key.DEFEND_FORWARD,
    ReportScore.key.DEFEND_BIG,
  ].map((score) => {
    const playerScore = scores.find((s) => s.key === score);
    let label = "";
    switch (score) {
      case ReportScore.key.DEFEND_POINT_GUARD:
        label = "Point Guard";
        break;
      case ReportScore.key.DEFEND_GUARD:
        label = "Guard";
        break;
      case ReportScore.key.DEFEND_WING:
        label = "Wing";
        break;
      case ReportScore.key.DEFEND_FORWARD:
        label = "Forward";
        break;
      case ReportScore.key.DEFEND_BIG:
        label = "Big";
        break;
    }

    return {
      key: score,
      value: playerScore?.value ?? 0,
      displayValue: playerScore
        ? `${Math.round((Number(playerScore?.value) / 2) * 100)}%`
        : 0,
      label,
    };
  });

  return (
    <ScoreBarChart
      labels={[0, 1, 2]}
      scores={chartData}
      dataKey="displayValue"
      print={print}
      tickFormatter={(value) => {
        switch (value) {
          case 0:
            return "None";
          case 1:
            return "Some";
          case 2:
            return "All";
        }
        return `${value}`;
      }}
    />
  );
}

function OtherSection({
  playerId,
  defaultSummary,
  scores,
}: {
  playerId: number;
  defaultSummary?: string;
  scores: PlayerMetadataScoreRow[];
}) {
  const { isScoutAdmin, isDeveloper } = useAccount();

  const hasEditAccess = isScoutAdmin || isDeveloper;

  const otherScores = SkillScoreSections.OTHER.map((section) => section.scores)
    .flat()
    .map((score) => ({
      key: score,
      value: scores?.find((s) => s.key === score)?.value ?? 0,
      label:
        ReportScoreDescriptions[score].fullLabel ||
        ReportScoreDescriptions[score].label,
    }));

  return (
    <Flex direction="down" gap="md">
      <AiEditor
        title="Other"
        defaultValue={defaultSummary}
        readonly={!hasEditAccess}
        maxLength={1200}
        onGenerateAi={async () => {
          const result = await apolloApi.generatePlayerSummary({
            playerId,
            type: "OTHER",
            size: "MEDIUM",
          });

          return result;
        }}
        onSave={async (value) => {
          await apolloApi.updatePlayerSummary({
            playerId,
            type: "OTHER",
            size: "MEDIUM",
            value,
          });

          return true;
        }}
      />
      {scores.length ? (
        <Flex direction="right" gap="md" wrap className="w-1/2">
          <ScoreBarChart labels={[1, 2, 3, 4, 5]} scores={otherScores ?? []} />
        </Flex>
      ) : null}
    </Flex>
  );
}
